/**
 * Validação de CPF e CNPJ.
 *
 * Fica em `contracts` porque os dois lados precisam da mesma resposta: o
 * formulário avisa antes de enviar, e a API recusa de qualquer forma. Validar
 * só no navegador deixa a regra a um `curl` de distância.
 *
 * O dígito verificador é conferido, não apenas o tamanho. Sem isso,
 * `11111111111` entra no cadastro e só aparece como problema no dia da emissão
 * da nota, quando já existe movimento amarrado ao registro.
 */

/** Remove tudo que não for dígito. Aceita entrada formatada da tela. */
export function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

function digitoVerificador(base: string, pesoInicial: number): number {
  let soma = 0;
  let peso = pesoInicial;

  for (let i = 0; i < base.length; i += 1) {
    soma += Number(base[i]) * peso;
    peso -= 1;
    if (peso < 2) peso = 9;
  }

  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

function todosIguais(valor: string): boolean {
  return new Set(valor).size === 1;
}

export function ehCpfValido(documento: string): boolean {
  const digitos = apenasDigitos(documento);
  if (digitos.length !== 11 || todosIguais(digitos)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i += 1) soma += Number(digitos[i]) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== Number(digitos[9])) return false;

  soma = 0;
  for (let i = 0; i < 10; i += 1) soma += Number(digitos[i]) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;

  return resto === Number(digitos[10]);
}

export function ehCnpjValido(documento: string): boolean {
  const digitos = apenasDigitos(documento);
  if (digitos.length !== 14 || todosIguais(digitos)) return false;

  const primeiro = digitoVerificador(digitos.slice(0, 12), 5);
  if (primeiro !== Number(digitos[12])) return false;

  const segundo = digitoVerificador(digitos.slice(0, 13), 6);
  return segundo === Number(digitos[13]);
}

/** Valida conforme o tipo de pessoa: CPF para PF, CNPJ para PJ. */
export function ehDocumentoValido(tipo: 'PF' | 'PJ', documento: string): boolean {
  return tipo === 'PF' ? ehCpfValido(documento) : ehCnpjValido(documento);
}
